import { CalendarCheck } from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { statusInfo } from "@/lib/appointments";
import { appointmentWhen } from "@/lib/contact-activity";
import { useAppointments } from "@/lib/queries";
import { cn } from "@/lib/utils";

/**
 * A contact's appointments still to come, scheduled or confirmed and not yet over: pinned over a
 * conversation's thread and the contact's timeline as the open callback is, and listed on the call
 * screen's card (ACL-559). Nothing when there is none, or none the reader may see: core-api shows a
 * rep only the appointments they host or booked.
 */
function useUpcoming(leadId: string, limit: number) {
  // Fixed when drawn, so a booking (which refetches every list) finds the same query.
  const [since] = React.useState(() => new Date().toISOString());
  return useAppointments(
    { lead_id: leadId, start: since, statuses: "scheduled,confirmed", limit },
    // Never another contact's appointment while this one's loads.
    { placeholderData: undefined },
  );
}

/** The next appointment, with the way to it on the Calendars page. `className` is for the box
 *  around it, drawn only when there is one. */
export function NextAppointment({ leadId, className }: { leadId: string; className?: string }) {
  const next = useUpcoming(leadId, 1);
  const a = next.data?.[0];
  if (!a) return null;
  const status = statusInfo(a.status);
  // A title left empty is the calendar's name and the contact's: the calendar once is enough.
  const calendar = a.title.startsWith(a.calendar_name) ? null : a.calendar_name;
  return (
    <div className={className}>
      <div role="status" aria-label="Next appointment" className="flex items-start gap-2.5 rounded-lg border border-accent-7 bg-accent-2 px-3 py-2.5">
        <CalendarCheck className="mt-0.5 size-4 shrink-0 text-accent-11" aria-hidden />
        <div className="min-w-0 space-y-0.5">
          <p className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-sm text-text">
            <span className="font-medium">Appointment</span>
            <span>{appointmentWhen(a)}</span>
            <Badge size="sm" tone={status.tone}>
              {status.label}
            </Badge>
          </p>
          <p className="text-xs text-text-muted">
            {[a.title, calendar, a.owner_name && `with ${a.owner_name}`].filter(Boolean).join(" · ")}
            {" · "}
            <Link to={`/calendars?tab=list&appointment=${a.id}`} className="font-medium text-accent-11 hover:underline">
              Open in Calendars
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

/** Appointments shown on the call screen's card; the rest are on the contact's Appointments tab. */
const ON_THE_CARD = 3;

/** The next few appointments on the call screen's card, a line each under the open callback, in the
 *  card's own small type. */
export function UpcomingAppointments({ leadId, className }: { leadId: string; className?: string }) {
  const upcoming = useUpcoming(leadId, ON_THE_CARD);
  const list = upcoming.data ?? [];
  if (list.length === 0) return null;
  return (
    <ul aria-label="Upcoming appointments" className={cn("space-y-0.5", className)}>
      {list.map((a) => (
        <li key={a.id} className="flex items-start gap-1 text-accent-11">
          <CalendarCheck className="mt-px size-3.5 shrink-0" aria-hidden />
          <span className="min-w-0">
            Appointment {appointmentWhen(a)} · {statusInfo(a.status).label}
            {a.owner_name && ` · with ${a.owner_name}`}
          </span>
        </li>
      ))}
    </ul>
  );
}
